import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Mic, FileText, Receipt, Landmark, Languages, Server } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { useLanguage } from "@/i18n";

const FACTS = [
  { icon: Landmark, title: "Conçu en Belgique", line: "Règles belges intégrées : TVA 6 % et 21 %, autoliquidation entre professionnels, mentions de l'A.R. n° 1, numérotation légale des factures." },
  { icon: Server, title: "Hébergé en Europe", line: "Vos chantiers, vos clients et vos prix d'achat restent chez un éditeur belge, joignable directement." },
  { icon: Languages, title: "Français et néerlandais", line: "L'interface, les documents clients et le support existent dans les deux langues du pays." },
];

const STEPS = [
  { icon: Mic, title: "Sur le chantier", line: "Vous dictez ce que vous voyez. Photos, mesures, plaque signalétique et matériel se rangent dans le dossier." },
  { icon: FileText, title: "Avant de repartir", line: "Le devis est chiffré avec vos prix, relu par l'assistant, signé par le client sur votre téléphone." },
  { icon: Receipt, title: "Au bureau", line: "Acompte, facture finale, UBL Peppol, suivi des règlements et relances automatiques des impayés." },
];

export default function AboutPage() {
  const { t } = useLanguage();

  useEffect(() => {
    const previousTitle = document.title;
    document.title = t("À propos de Devisto — le logiciel belge du devis à la facture");
    const meta = document.querySelector('meta[name="description"]');
    const previousMeta = meta?.getAttribute("content");
    meta?.setAttribute(
      "content",
      t("Devisto est né sur les chantiers, chez un chauffagiste belge. Un logiciel 100 % belge qui prépare le devis pendant la visite et va jusqu'à la facture électronique Peppol."),
    );
    return () => {
      document.title = previousTitle;
      if (meta && previousMeta) meta.setAttribute("content", previousMeta);
    };
  }, [t]);

  return (
    <div data-testid="about-page" className="font-body bg-white text-[#0F2645] min-h-screen flex flex-col">
      <Header />

      <main className="flex-1">
        {/* En-tête */}
        <section
          className="relative overflow-hidden pt-32 pb-20 text-white sm:pt-40"
          style={{ background: "linear-gradient(168deg, #143157 0%, #0F2645 55%, #0A1A31 100%)" }}
        >
          <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
          </div>
          <div className="relative max-w-3xl mx-auto px-6 lg:px-8">
            <span className="inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/[0.07] px-4 py-1.5 text-[11px] font-bold uppercase tracking-[0.16em] text-white">
              <span className="inline-flex h-3 w-[13px] shrink-0 overflow-hidden rounded-[2px]" aria-hidden="true">
                <span className="h-full w-1/3 bg-[#1A1A1A]" />
                <span className="h-full w-1/3 bg-[#FFD100]" />
                <span className="h-full w-1/3 bg-[#C8102E]" />
              </span>
              {t("À propos")}
            </span>
            <h1 data-testid="about-title" className="mt-7 font-heading text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.06]">
              {t("Devisto est né sur les chantiers, pas dans un bureau")}
            </h1>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-[#C8D2EC]">
              {t("Un logiciel pensé par un chauffagiste belge pour les entrepreneurs du bâtiment : préparer le devis pendant la visite, et aller jusqu'à la facture électronique sans rien retaper.")}
            </p>
          </div>
        </section>

        {/* Histoire */}
        <section className="py-20 sm:py-24">
          <div className="max-w-3xl mx-auto px-6 lg:px-8">
            <Reveal>
              <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">{t("Notre histoire")}</span>
              <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
                {t("Six ans de chantiers, un problème qui revenait chaque soir")}
              </h2>
              <div className="mt-7 space-y-5 text-base leading-relaxed text-[#0F2645]">
                <p>
                  {t("Après six années dans le chauffage, le même scénario se répétait : sur place, des photos, des mesures et des notes sur un bloc-notes. Puis, une fois rentré à la maison, il fallait reprendre toutes ces informations, de mémoire, pour taper le devis.")}
                </p>
                <p>
                  {t("Un oubli de sept mètres de tubage, une durée de pose sous-estimée, et la marge du chantier partait en fumée. Les logiciels existants commençaient tous par la facture — c'est-à-dire par la fin.")}
                </p>
                <p>
                  {t("Devisto a été construit dans l'autre sens : d'abord l'assistant qui écoute la visite et crée le dossier, ensuite le devis avec vos prix, la signature du client, le rapport d'intervention, et enfin la facture électronique.")}
                </p>
                <p className="font-heading text-lg font-semibold text-[#0F2645]">
                  {t("Moins de notes. Moins d'oublis. Plus de temps. Un devis mieux préparé.")}
                </p>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Le parcours */}
        <section className="bg-[#F6F8FD] py-20 sm:py-24">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">{t("Ce que Devisto fait concrètement")}</h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {STEPS.map((s, i) => (
                <div key={s.title} data-testid={`about-step-${i}`} className="rounded-2xl border border-[#E2E7F7] bg-white p-6">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#EEF1FC] text-[#3A5CC8]">
                    <s.icon className="w-[18px] h-[18px]" strokeWidth={1.9} />
                  </span>
                  <p className="mt-4 font-heading text-base font-bold">{t(s.title)}</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#5C6A87]">{t(s.line)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 100 % belge */}
        <section className="py-20 sm:py-24">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#3A5CC8]">{t("100 % belge")}</span>
            <h2 className="mt-5 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
              {t("Un éditeur belge, des règles belges")}
            </h2>
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-5">
              {FACTS.map((f, i) => (
                <div key={f.title} data-testid={`about-fact-${i}`} className="rounded-2xl border border-[#E2E7F7] bg-white p-6">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#EEF1FC] text-[#3A5CC8]">
                    <f.icon className="w-[18px] h-[18px]" strokeWidth={1.9} />
                  </span>
                  <p className="mt-4 font-heading text-base font-bold">{t(f.title)}</p>
                  <p className="mt-2 text-sm leading-relaxed text-[#5C6A87]">{t(f.line)}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-24">
          <div className="max-w-5xl mx-auto px-6 lg:px-8">
            <div
              className="relative overflow-hidden rounded-2xl p-10 sm:p-14 text-center text-white"
              style={{ background: "linear-gradient(168deg, #143157 0%, #0F2645 55%, #0A1A31 100%)" }}
            >
              <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
              </div>
              <h2 className="relative font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">
                {t("Essayez sur votre prochain chantier")}
              </h2>
              <p className="relative mt-4 text-base text-[#C8D2EC]">{t("30 jours gratuits, sans carte bancaire. Ensuite 25 €/mois par société, ouvriers inclus.")}</p>
              <Link
                to="/studio/inscription"
                data-testid="about-cta"
                className="relative mt-8 inline-flex items-center gap-2 rounded-xl bg-[#3A5CC8] px-7 py-4 text-sm font-bold text-white hover:bg-[#3A5CC8] active:scale-[0.99] transition-all duration-200"
              >
                {t("Créer mon compte artisan")} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
